import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addMinutes, roundToNearestMinutes } from "date-fns";
import * as Yup from "yup";

import { CreateAppointmentRequest, appointmentsApi } from "@/api";
import {
  deriveAvailability,
  isKnownPurpose,
  toAllDayEnd,
  toAllDayStart,
} from "@/common/components/appointments/shared/AppointmentForm.utils";
import {
  DefaultAppointmentDurationMinutes,
  DefaultAppointmentStartStepMinutes,
} from "@/common/components/appointments/shared/appointmentConstants";
import { useAppointmentFormOptions } from "@/common/components/appointments/shared/hooks/useAppointmentFormOptions";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { nameof } from "@/common/helper/commonHelper";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { AppointmentCreationFormValues } from "../AppointmentCreation.types";

export function useAppointmentCreation(onClose: () => void, initialCounselorId?: string | null) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const { currentUser } = useCurrentUserContext();
  const queryClient = useQueryClient();
  const { validationSchema: appointmentValidationSchema, ...fieldOptions } = useAppointmentFormOptions();

  const validationSchema = useMemo(
    () =>
      appointmentValidationSchema.shape<{ [Property in keyof AppointmentCreationFormValues]?: Yup.AnySchema }>({
        counselorId: Yup.string()
          .nullable()
          .required(t("common.validation.fieldRequired", { fieldName: t("calendar.appointmentCreation.counselor") })),
      }),
    [appointmentValidationSchema, t]
  );

  const createAppointmentMutation = useMutation({
    mutationFn: (request: CreateAppointmentRequest) =>
      appointmentsApi.createAppointment({ createAppointmentRequest: request }),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
      showSuccess(t("calendar.appointmentCreation.successMessage"));
      onClose();
    },
  });

  const getDefaultDates = () => {
    const defaultStartDate = roundToNearestMinutes(new Date(), {
      nearestTo: DefaultAppointmentStartStepMinutes,
      roundingMethod: "ceil",
    });
    const defaultEndDate = addMinutes(defaultStartDate, DefaultAppointmentDurationMinutes);

    return { defaultStartDate, defaultEndDate };
  };

  const { defaultStartDate, defaultEndDate } = getDefaultDates();

  const formik = useFormikWithDirtyForm<AppointmentCreationFormValues>({
    initialValues: {
      isAllDay: false,
      startDate: defaultStartDate,
      endDate: defaultEndDate,
      purposeKey: "",
      location: null,
      details: "",
      client: null,
      notifyBySms: true,
      notifyByEmail: false,
      clientMarkedForRemoval: false,
      counselorId: initialCounselorId ?? currentUser.counselorId ?? null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.counselorId) {
        setSubmitting(false);
        return;
      }

      const availability = deriveAvailability(values.purposeKey, fieldOptions.purposes);
      const notifyBySms = !!values.client && values.notifyBySms;
      const notifyByEmail = !!values.client && values.notifyByEmail;

      const request: CreateAppointmentRequest = {
        purposeKey: values.purposeKey,
        availability,
        startTimeUtc: values.startDate,
        endTimeUtc: values.endDate,
        assignedCounselorId: values.counselorId,
        isAllDay: values.isAllDay,
        originTimeZoneId: values.isAllDay || notifyBySms || notifyByEmail ? getLocalTimeZoneId() : undefined,
        location: values.location,
        details: values.details || null,
        clientId: values.client?.clientId ?? null,
        notifyBySms,
        notifyByEmail,
      };

      try {
        await createAppointmentMutation.mutateAsync(request);
      } catch {
        // A rejected save (e.g. a failed notification aborting the create) is reported by the
        // global error handling; release the form so the user can adjust and retry.
        setSubmitting(false);
      }
    },
  });

  // The submit button only becomes enabled once all mandatory fields are filled. The date and time
  // fields are prefilled, so the purpose (and the location it requires) and the counselor are what
  // can be empty.
  const isSubmitDisabled =
    !formik.values.purposeKey ||
    !formik.values.counselorId ||
    (isKnownPurpose(formik.values.purposeKey, fieldOptions.purposes) && !formik.values.location);

  const handleIsAllDayChange = (checked: boolean) => {
    void formik.setFieldValue(nameof<AppointmentCreationFormValues>("isAllDay"), checked);

    if (checked) {
      void formik.setFieldValue(
        nameof<AppointmentCreationFormValues>("startDate"),
        toAllDayStart(formik.values.startDate)
      );
      void formik.setFieldValue(nameof<AppointmentCreationFormValues>("endDate"), toAllDayEnd(formik.values.endDate));
    } else {
      const { defaultStartDate: newStart, defaultEndDate: newEnd } = getDefaultDates();
      void formik.setFieldValue(nameof<AppointmentCreationFormValues>("startDate"), newStart);
      void formik.setFieldValue(nameof<AppointmentCreationFormValues>("endDate"), newEnd);
    }
  };

  return { formik, fieldOptions, handleIsAllDayChange, isSubmitDisabled };
}
